import { Dispatch, SetStateAction, type JSX } from 'react';
import { useIntl } from 'react-intl';
import DOMPurify from 'isomorphic-dompurify';
import IconButton from '../IconButton/IconButton';
import Overflow from '../../icons/Overflow';
import DropdownMenu from '../DropdownMenu/DropdownMenu';
import { DropdownMenuOption, LightboxDropdownMenuState } from '../../types';
import useIsMobileSizedScreen from '../../hooks/useIsMobileSizedScreen';
import styles from '../LightboxHeader/styles/styles.module.scss';

export type LightboxFooterProps = {
  photoTitle: string;
  attribution?: string;
  onDropdownMenuClick?: () => void;
  dropdownMenu?: LightboxDropdownMenuState;
  setDropdownMenuOptions?: Dispatch<SetStateAction<LightboxDropdownMenuState>>;
  dropdownOptions?: DropdownMenuOption[];
  shouldHideActions?: boolean;
};

// https://www.figma.com/file/KYgIXAiEuiuBQoIQ5Tm3qf/Lightbox?type=design&node-id=11-6310&mode=design&t=7z0lakmFOLdgvB7t-0
const LightboxFooter = ({
  photoTitle,
  attribution,
  onDropdownMenuClick,
  dropdownMenu,
  setDropdownMenuOptions,
  dropdownOptions,
  shouldHideActions
}: LightboxFooterProps): JSX.Element => {
  const intl = useIntl();
  const isMobile = useIsMobileSizedScreen();

  const sanitizedAttribution = attribution
    ? DOMPurify.sanitize(attribution, {
        ALLOWED_TAGS: ['a', 'strong'],
        ALLOWED_ATTR: ['href'],
        KEEP_CONTENT: true
      })
    : '';

  return (
    <div className={styles.footer}>
      {attribution ? (
        <div className={styles.attribution} dangerouslySetInnerHTML={{ __html: sanitizedAttribution }} />
      ) : (
        <div className={styles.title}>{photoTitle}</div>
      )}
      {isMobile && dropdownMenu && dropdownOptions && dropdownOptions.length > 0 && setDropdownMenuOptions && !shouldHideActions ? (
        <DropdownMenu
          anchor={
            <IconButton
              icon={{ Component: Overflow }}
              onClick={onDropdownMenuClick}
              title={intl.formatMessage({ defaultMessage: 'More actions' })}
              testId="LightboxMenu_Button"
              className={styles.iconButton}
              variant="transparent"
              size="sm"
            />
          }
          isOpen={dropdownMenu.isOpen}
          onCloseRequest={() => setDropdownMenuOptions({ isOpen: false, options: [] })}
          options={dropdownMenu?.options}
          placement="top-end"
        />
      ) : null}
    </div>
  );
};

export default LightboxFooter;
